import { Quote } from "../../api/Quote/Quote";
import { Grid, Box, useTheme, useMediaQuery, Button } from "@material-ui/core";
import QuoteTopSlider from "./components/QuoteTopSlider";
import QuoteTopTitle from "./components/QuoteTopTitle";
import QuoteTopPrice from "./components/QuoteTopPrice";
import QuoteTopPriceOptions from "./components/QuoteTopPriceOptions";
import QuoteTopActions from "./components/QuoteTopActions";
import { hasQuoteSubscriptions, paymentTypeTitle, quoteBalanceOutstanding, quotePaymentsMade } from "../../app/utils";
import { QuoteHr } from "./components/QuoteHr";
import { createCustomerPortalRedirect } from "../../api/SwyftApi";
import { EEventType, eventAnalytic } from "../../app/analytics";
import { EventSlot } from "../../api/EventSlot/EventSlot";
import { format } from "date-fns";
import { TF } from "../../components/typography/TF";

export const QuoteTop = ({ quote, payNextPageLink, isAvailableSchedule, selectedEventSlot }: Props) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  if (!quote) {
    return null;
  }

  const handleViewPortal = async () => {
    eventAnalytic.record(EEventType.StripeCustomerPortal);
    const portalUrl = await createCustomerPortalRedirect(quote.id);

    window.location.href = portalUrl;
  };

  const outstandingBalance = quoteBalanceOutstanding(quote);
  const paymentsMade = quotePaymentsMade(quote);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={6}>
        <Box maxWidth={isSmall ? "250px" : "320px"} margin="auto">
          <QuoteTopSlider lineItems={quote.activeItems} />
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Grid container spacing={isSmall ? 1 : 2}>
          <Grid item xs={12}>
            <Box mt={isSmall ? 3 : 8} textAlign={isSmall ? "center" : "left"}>
              <Box>
                <QuoteTopTitle quoteTitle={quote.title} />
              </Box>
              <Box mt={isSmall ? 1 : 0}>
                <QuoteTopPrice quote={quote} />
              </Box>
              <Box mt={isSmall ? 1 : 0}>
                <QuoteTopPriceOptions quote={quote} textAlign={isSmall ? "center" : "left"} />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            {paymentsMade.length > 0 && false && (
              <Box>
                <TF variant="body1" bold>
                  Status
                </TF>
                <QuoteHr />
                {paymentsMade.map((p, i) => (
                  <Box display="flex" justifyContent="space-between" my={1} key={i}>
                    <Box flexGrow={1}>
                      <TF>{p.amount === quote.depositRequirement ? "Deposit Paid" : "Payment"}</TF>
                    </Box>
                    <Box>
                      <TF format="price">{p.amount * -1}</TF>
                    </Box>
                  </Box>
                ))}
                <Box display="flex" justifyContent="space-between" my={1}>
                  <Box flexGrow={1}>
                    <TF bold>Balance Due</TF>
                  </Box>
                  <Box>
                    <TF variant="subtitle2" format="price" bold>
                      {outstandingBalance}
                    </TF>
                  </Box>
                </Box>
                {hasQuoteSubscriptions(quote.subscriptions) &&
                  quote.subscriptions!.map((s, i) => (
                    <Box key={i + "subscription"}>
                      <Box display="flex" justifyContent="space-between" my={1}>
                        <Box flexGrow={1}>
                          <TF>{s.name}</TF>
                        </Box>
                        <Box>
                          <TF format="price">{s.price}</TF>
                          &nbsp;{paymentTypeTitle(s.paymentType, true)}
                        </Box>
                      </Box>
                      <Box display="flex">
                        <Box flexGrow={1}>View your subscription package</Box>
                        <Box>
                          <Button onClick={handleViewPortal} size="small" color="secondary">
                            View
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                {selectedEventSlot && (
                  <Box>
                    <QuoteHr />
                    <Box display="flex" justifyContent="space-between" my={1}>
                      <Box flexGrow={1}>
                        <TF>Installation date</TF>
                      </Box>
                      <TF bold>
                        {format(new Date(selectedEventSlot!.startDate), "MMMM")}{" "}
                        {format(new Date(selectedEventSlot!.startDate), "d")}
                      </TF>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            {paymentsMade.length === 0 && (
              <Box mt={3} mb={3}>
                <QuoteTopActions
                  quote={quote}
                  isAvailableSchedule={isAvailableSchedule}
                  payNextPageLink={payNextPageLink}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

interface Props {
  quote: Quote;
  payNextPageLink: string;
  isAvailableSchedule: boolean;
  selectedEventSlot?: EventSlot | null;
}
